import { z } from 'zod'
import { ApiError, type ApiClient } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Stream config — retention and replication. ONE object per level, not per
 * stream (docs/inventory/storage-capture.json R1GET):
 *
 *   GET /{clusterId}/mgmt/core/stream-config                 cluster default   OBSERVED
 *     → { TStamp, data: { replication: 1, retention: 90 }, message: 'success', status: 'success' }
 *   PUT /{clusterId}/mgmt/core/stream-config                 write the default  R1PUT
 *   GET /{clusterId}/mgmt/scope/{scopeId}/stream-config      scope override     R2
 *   PUT /{clusterId}/mgmt/scope/{scopeId}/stream-config      write the override R2
 *
 * That single object is why every stream-management row reports the same
 * retention / replication: the per-stream value is the cluster default (or
 * the scope override — precedence is CB-33, still open). The three other
 * calls answered `{ message: 'user not authorised', status: 'failed',
 * status_code: 400 }` in an HTTP 200 body for an Organization Admin /
 * Security Engineer (R1PUT, R2): both writes and the scope read need a
 * higher role — Root or TenantAdmin, which one is unknown. Their body shape
 * is the legacy openapi's `{ retention, replication }` (no stream name) and
 * stays UNTESTED until a capture from such a session lands.
 *
 * The refusal is a typed error, `StreamConfigForbidden`, so the Retention
 * tab can say "needs a higher role" instead of "request failed". The client
 * core already turns that exact envelope into an `ApiError(403)`; this module
 * narrows it (and re-checks the envelope itself, in case a caller bypasses
 * the client's check) so the type is reliable at every level.
 */

const UNAUTHORISED = 'user not authorised'

/** The server refused with `status: 'failed', message: 'user not authorised'`. */
export class StreamConfigForbidden extends ApiError {
  constructor(what: string, body: unknown) {
    super(403, `${what} needs a higher role — the server said "${UNAUTHORISED}".`, body, UNAUTHORISED)
    this.name = 'StreamConfigForbidden'
  }
}

export const isStreamConfigForbidden = (e: unknown): e is StreamConfigForbidden => e instanceof StreamConfigForbidden

/** Retention in days and the replication factor; null when the server sent nothing usable. */
export interface StreamConfig {
  retention: number | null
  replication: number | null
}

export interface StreamConfigInput extends StreamConfig {
  clusterId: string
}

export interface ScopeStreamConfigInput extends StreamConfigInput {
  scopeId: string
}

const numOrNull = z.preprocess(
  (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null),
  z.number().nullable(),
)

/** The `data` object of a GET. Loose: extra keys pass, a missing number is null. */
export const streamConfigData = z
  .object({ retention: numOrNull.default(null), replication: numOrNull.default(null) })
  .passthrough()

export const streamConfigResponse = ackEnvelope.extend({
  TStamp: z.string().optional(),
  data: streamConfigData.optional(),
})

export type StreamConfigResponse = z.output<typeof streamConfigResponse>

export interface StreamConfigApi {
  /** Cluster default. */
  get: (q: { clusterId: string }) => Promise<StreamConfig>
  /** Write the cluster default. R1PUT — refused for a Security Engineer. */
  put: (q: StreamConfigInput) => Promise<void>
  /** Scope override. R2 — refused for a Security Engineer. */
  getScope: (q: { clusterId: string; scopeId: string }) => Promise<StreamConfig>
  /** Write the scope override. R2. */
  putScope: (q: ScopeStreamConfigInput) => Promise<void>
}

const body = ({ retention, replication }: StreamConfig) => ({ retention, replication })

/** Re-throw the client's 403 (or the raw refusal envelope) as the typed error. */
function typed(e: unknown, what: string): unknown {
  if (e instanceof StreamConfigForbidden) return e
  if (e instanceof ApiError && (e.status === 403 || e.serverMessage === UNAUTHORISED)) {
    return new StreamConfigForbidden(what, e.body)
  }
  return e
}

function assertAllowed(res: { status?: string; message?: string }, what: string): void {
  if (res.status === 'failed' && res.message === UNAUTHORISED) throw new StreamConfigForbidden(what, res)
  assertAck(res, what)
}

async function guarded<T>(what: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (e) {
    throw typed(e, what)
  }
}

const config = (res: StreamConfigResponse): StreamConfig => ({
  retention: res.data?.retention ?? null,
  replication: res.data?.replication ?? null,
})

export function createStreamConfigApi(client: ApiClient): StreamConfigApi {
  const read = (what: string, path: string) =>
    guarded(what, async () => {
      const res = await client.request('GET', path, { schema: streamConfigResponse })
      assertAllowed(res, what)
      return config(res)
    })
  const write = (what: string, path: string, values: StreamConfig) =>
    guarded(what, async () => {
      const res = await client.request('PUT', path, { body: body(values), schema: ackEnvelope })
      assertAllowed(res, what)
    })

  return {
    get: ({ clusterId }) => read('Reading the cluster stream config', `/${clusterId}/mgmt/core/stream-config`),
    put: (q) => write('Saving the cluster stream config', `/${q.clusterId}/mgmt/core/stream-config`, q),
    getScope: ({ clusterId, scopeId }) =>
      read('Reading the scope stream config', `/${clusterId}/mgmt/scope/${scopeId}/stream-config`),
    putScope: (q) =>
      write('Saving the scope stream config', `/${q.clusterId}/mgmt/scope/${q.scopeId}/stream-config`, q),
  }
}
